import { designTokens } from '@app/theme';
import { Button, Drawer, Form, Input, InputNumber, Space, Switch, Tag, Typography } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { SettingResponse } from '@api/generated/types.gen';

import { isNumericType, settingTypeTagColor } from '../setting-type';

const { Text } = Typography;

type EditorFormValues = {
  value: string | number | boolean;
};

type SettingEditorProps = {
  setting: SettingResponse | null;
  saving: boolean;
  onCancel: () => void;
  onSave: (key: string, value: string) => void;
};

/**
 * Боковой drawer для редактирования значения настройки.
 *
 * Подбирает тип инпута по `setting.type`:
 * - `INT` / `LONG` → `InputNumber` с целочисленной валидацией;
 * - `BOOLEAN` → `Switch`;
 * - `STRING` → `TextArea` с автоподбором высоты (значения бывают многострочными).
 *
 * Пустое значение допустимо только для `STRING`: многие настройки трактуют «пусто»
 * как «не задано» / «без ограничения» (allowlist подсетей, креды провайдеров,
 * минимальная версия приложения). Числовые настройки обязательны.
 *
 * На узких экранах drawer раскрывается на всю ширину.
 */
export function SettingEditor({ setting, saving, onCancel, onSave }: SettingEditorProps) {
  const { t } = useTranslation(['settings', 'common', 'errors']);
  const [form] = Form.useForm<EditorFormValues>();

  useEffect(() => {
    if (setting) {
      const raw = setting.value ?? '';
      const initialValue: EditorFormValues['value'] = isNumericType(setting.type)
        ? Number(raw || 0)
        : setting.type === 'BOOLEAN'
          ? raw === 'true'
          : raw;
      form.setFieldsValue({ value: initialValue });
    }
  }, [setting, form]);

  const onFinish = (values: EditorFormValues): void => {
    if (!setting?.key) return;
    onSave(setting.key, String(values.value));
  };

  return (
    <Drawer
      title={t('settings:edit.title')}
      placement="right"
      width="min(420px, 100vw)"
      open={setting !== null}
      onClose={onCancel}
      destroyOnHidden
      footer={
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onCancel}>{t('common:actions.cancel')}</Button>
          <Button
            type="primary"
            loading={saving}
            onClick={() => {
              form.submit();
            }}
          >
            {t('common:actions.save')}
          </Button>
        </Space>
      }
    >
      {setting && (
        <Form<EditorFormValues> form={form} layout="vertical" onFinish={onFinish}>
          <Space direction="vertical" size={4} style={{ marginBlockEnd: 24 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('settings:columns.key')}
            </Text>
            <Text
              code
              style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: 13,
                background: designTokens.slate50,
                padding: '2px 6px',
                borderRadius: 4,
              }}
            >
              {setting.key}
            </Text>
            {setting.description && (
              <Text type="secondary" style={{ fontSize: 13, marginTop: 8 }}>
                {setting.description}
              </Text>
            )}
            <div style={{ marginTop: 8 }}>
              <Tag color={settingTypeTagColor(setting.type)}>
                {t(`settings:types.${setting.type ?? 'STRING'}`, {
                  defaultValue: setting.type,
                })}
              </Tag>
            </div>
            <Text
              type="secondary"
              style={{
                fontSize: 12,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                overflowWrap: 'anywhere',
              }}
            >
              {t('settings:edit.currentValue', { value: setting.value })}
            </Text>
          </Space>

          {setting.type === 'BOOLEAN' ? (
            <Form.Item label={t('settings:edit.valueLabel')} name="value" valuePropName="checked">
              <Switch
                checkedChildren={t('settings:boolean.true')}
                unCheckedChildren={t('settings:boolean.false')}
              />
            </Form.Item>
          ) : (
            <Form.Item
              label={t('settings:edit.valueLabel')}
              name="value"
              rules={
                isNumericType(setting.type)
                  ? [{ required: true, message: t('errors:fields.NOT_BLANK') }]
                  : undefined
              }
            >
              {isNumericType(setting.type) ? (
                <InputNumber<number>
                  style={{
                    width: '100%',
                    fontFamily: '"JetBrains Mono", monospace',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                  min={0}
                  step={1}
                  precision={0}
                  autoFocus
                />
              ) : (
                <Input.TextArea
                  autoSize={{ minRows: 2, maxRows: 12 }}
                  style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 13 }}
                  autoFocus
                />
              )}
            </Form.Item>
          )}
        </Form>
      )}
    </Drawer>
  );
}
